import { inject, Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Subscription } from 'rxjs';
import { ConfigClientService } from '../core/api';
import { NotificationReminderOptionDto } from '../core/api/model/notificationReminderOptionDto';
import { PasswordPolicyService } from '../core/auth/services/password-policy.service';
import { AuthService } from './auth.service';

/**
 * Preloads config used after login: notification reminder options and password policy.
 * Subscribes to userAuthenticated$ and triggers both loads in the background (non-blocking).
 * Inject once at app init so it subscribes before auth runs.
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigPreloadService implements OnDestroy {
  private readonly _authService = inject(AuthService);
  private readonly _configClient = inject(ConfigClientService);
  private readonly _passwordPolicyService = inject(PasswordPolicyService);

  private readonly _subscription = new Subscription();

  private _cachedNotificationOptions: NotificationReminderOptionDto[] | null = null;
  private _notificationOptionsLoadPromise: Promise<NotificationReminderOptionDto[]> | null = null;

  constructor() {
    this._subscription.add(
      this._authService.userAuthenticated$.subscribe(() => {
        this.preloadNonBlocking();
      })
    );
    this._subscription.add(
      this._authService.userLoggedOut$.subscribe(() => {
        this._cachedNotificationOptions = null;
        this._notificationOptionsLoadPromise = null;
        this._passwordPolicyService.clearCache();
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private preloadNonBlocking(): void {
    void this.loadNotificationOptions();
    void this._passwordPolicyService.getPolicy();
  }

  private loadNotificationOptions(): Promise<NotificationReminderOptionDto[]> {
    if (this._cachedNotificationOptions !== null) {
      return Promise.resolve(this._cachedNotificationOptions);
    }
    if (this._notificationOptionsLoadPromise !== null) {
      return this._notificationOptionsLoadPromise;
    }
    this._notificationOptionsLoadPromise = firstValueFrom(this._configClient.getNotificationReminderOptions())
      .then((options) => {
        this._cachedNotificationOptions = options;
        return options;
      })
      .catch(() => {
        this._cachedNotificationOptions = [];
        return [];
      });
    return this._notificationOptionsLoadPromise;
  }

  /** Cached notification reminder options, or null if not loaded yet. */
  public getCachedNotificationOptions(): NotificationReminderOptionDto[] | null {
    return this._cachedNotificationOptions;
  }

  /**
   * Returns notification options, loading from API if needed. Safe to call without awaiting (non-blocking).
   */
  public getNotificationOptions(): Promise<NotificationReminderOptionDto[]> {
    return this.loadNotificationOptions();
  }
}
